import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import watchService from '../../services/watch.service'
import { FaArrowLeftLong } from "react-icons/fa6";

export function isEmpty(value) {
    if (
        value === undefined ||
        value === null ||
        (typeof value === 'object' && Object.keys(value).length === 0) ||
        (typeof value === 'string' && value.trim().length === 0)
    ) {
        return true;
    } else {
        return false;
    }
}


const WatchDetails = () => {
    const { watchNo } = useParams()
    const navigate = useNavigate()
    // const watchNo = localStorage.getItem('invicta-watch-no') || '0050'
    const [searchModel, setSearchModel] = useState('')
    const [audioProgress, setAudioProgress] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [watchModelDetails, setWatchModelDetails] = useState({})
    const [watchStory, setWatchStory] = useState(null)
    const [modelNumber, setModelNumber] = useState(watchNo)
    const [loading, setLoading] = useState(true)
    const [storyLoader, setStoryLoader] = useState(true)
    const [isDetailsScrolling, setIsDetailsScrolling] = useState(false)
    const scrollTimeoutRef = useRef(null)
    const audioRef = useRef(null)
    const progressBarRef = useRef(null)
    // Update search input when watchNo changes
    useEffect(() => {
        setWatchStory("")
        setSearchModel('')
    }, [watchNo])
    const getWatchModelDetails = useCallback(async () => {
        try {
            setLoading(true)
            const response = await watchService.getWatchModelDetails(watchNo)
            const specifications = response?.specifications || {}
            setWatchModelDetails(specifications)
            setModelNumber(response?.model_number || specifications.model_no || watchNo)
        } catch (error) {
            console.error('Error fetching watch details:', error)
            setWatchModelDetails({})
            setModelNumber(watchNo)
        } finally {
            setLoading(false)
        }
    }, [watchNo])
    const getWatchModelStory = useCallback(async () => {
        try {
            setStoryLoader(true)
            const response = await watchService.getWatchModelStory(watchNo)
            setWatchStory(response)
            if (response?.model_number) {
                setModelNumber(response.model_number)
            }
        } catch (error) {
            console.error('Error fetching watch details:', error)
            setWatchStory(null)
        } finally {
            setStoryLoader(false)
        }
    }, [watchNo])
    useEffect(() => {
        getWatchModelDetails()
        getWatchModelStory()
    }, [getWatchModelDetails, getWatchModelStory])
    const handleSearch = () => {
        if (searchModel.trim()) {
            const searchedModelNo = searchModel.trim()
            localStorage.setItem('invicta-watch-no', searchedModelNo)
            // Navigate to the new watch number route, which will trigger API call
            navigate(`/watch-details/${searchedModelNo}`)
        }
    }
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }


    const activeDescription = useMemo(() => {
        return watchStory?.script || watchStory?.description || watchModelDetails?.description || ''
    }, [watchStory, watchModelDetails])

    const audioSource = watchStory?.audio_url

    const specificationEntries = useMemo(() => {
        if (!watchModelDetails || typeof watchModelDetails !== 'object') return []
        return Object.entries(watchModelDetails).filter(([, value]) => value !== null && value !== undefined && value !== '')
    }, [watchModelDetails])

    const watchImage = watchModelDetails?.image || watchModelDetails?.image_url

    useEffect(() => {
        const audioElement = audioRef.current
        if (!audioElement || !audioSource) return
        const handleTimeUpdate = () => {
            setCurrentTime(audioElement.currentTime)
            if (audioElement.duration) {
                setAudioProgress((audioElement.currentTime / audioElement.duration) * 100)
            }
        }
        const handleLoadedMetadata = () => {
            setDuration(audioElement.duration || 0)
        }
        const handleEnded = () => {
            setIsPlaying(false)
            setAudioProgress(0)
            setCurrentTime(0)
        }
        audioElement.addEventListener('timeupdate', handleTimeUpdate)
        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata)
        audioElement.addEventListener('ended', handleEnded)
        return () => {
            audioElement.removeEventListener('timeupdate', handleTimeUpdate)
            audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
            audioElement.removeEventListener('ended', handleEnded)
        }
    }, [audioSource])
    useEffect(() => {
        const audioElement = audioRef.current
        if (!audioElement) return
        audioElement.pause()
        audioElement.currentTime = 0
        setIsPlaying(false)
        setAudioProgress(0)
        setCurrentTime(0)
        setDuration(0)
    }, [audioSource])
    const formatTime = (seconds) => {
        if (!seconds || Number.isNaN(seconds)) return '00:00'
        const rounded = Math.floor(seconds)
        const mins = Math.floor(rounded / 60)
        const secs = rounded % 60
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    }
    const togglePlay = () => {
        const audioElement = audioRef.current
        if (!audioElement || !audioSource) return
        if (isPlaying) {
            audioElement.pause()
            setIsPlaying(false)
        } else {
            audioElement.play()
            setIsPlaying(true)
        }
    }
    const handleSeek = (event) => {
        const audioElement = audioRef.current
        const progressElement = progressBarRef.current
        if (!audioElement || !progressElement || !duration) return
        const rect = progressElement.getBoundingClientRect()
        const clickX = event.clientX - rect.left
        const ratio = Math.min(Math.max(clickX / rect.width, 0), 1)
        const newTime = ratio * duration
        audioElement.currentTime = newTime
        setCurrentTime(newTime)
        setAudioProgress(ratio * 100)
    }
    const handleDetailsScroll = () => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current)
        }
        setIsDetailsScrolling(true)
        scrollTimeoutRef.current = setTimeout(() => {
            setIsDetailsScrolling(false)
        }, 700)
    }
    if (loading) {
        return (
            <div className="w-screen h-screen bg-[#FEF20F] relative overflow-x-hidden flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 bg-white p-5 rounded-[15px] ">
                    <div className="w-10 h-10 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin "></div>
                </div>
            </div>
        )
    }
    return (
        <div className="w-full h-screen bg-[#FEF20F] relative overflow-x-hidden  pt-16 sm:pt-0 overflow-y-hidden no-scrollbar">
            {/* Back Button */}
            <button
                onClick={() => navigate("/")}
                className="fixed top-4 left-6 z-10 flex items-center gap-2 bg-white rounded-full px-5 py-2.5 text-[#B38B00] font-medium shadow-sm border border-transparent hover:border-[#B38B00]/30 hover:shadow-md transition-all"
            >
                <FaArrowLeftLong />


                <span>Back</span>
            </button>
            {/* Search Bar at Top */}
            <div className="w-full px-4 sm:px-6 pt-6 pb-4 flex justify-center mt-4 sm:mt-14">
                <div className="relative w-full flex gap-2 sm:gap-3 flex-row max-w-[950px]">
                    <input
                        type="text"
                        value={searchModel}
                        onChange={(e) => setSearchModel(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Search model number"
                        className="w-full h-12 sm:h-14 max-tab:placeholder:text-sm bg-white border-0 rounded-lg px-4 sm:px-5 pr-12 text-lg sm:text-xl text-gray-800 shadow-md outline-none focus:shadow-lg placeholder:text-gray-400"
                    />
                    <button
                        onClick={handleSearch}
                        className="w-auto justify-center bg-white border-0 rounded-lg px-4 py-3 sm:py-0 text-[#B38B00] flex items-center shadow-md sm:shadow-none "
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>
            </div>

            {!isEmpty(watchModelDetails) && modelNumber && <h1 className="text-2xl sm:text-3xl font-semibold text-black mb-4 max-w-[950px] mx-auto px-4 sm:px-0">{modelNumber}</h1>}

            {/* Main Content Card */}
            {!isEmpty(watchModelDetails) ? <div className="w-full max-w-5xl mx-auto px-6 ">
                <div className="bg-white rounded-lg shadow-xl p-6 rounded-[15px]">
                    {/* Title */}
                    {/* <h1 className="text-4xl font-bold text-black mb-8">{modelNumber}</h1> */}

                    <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 max-h-[calc(100vh-270px)] overflow-y-auto auto-scrollbar  rounded ${isDetailsScrolling ? 'scrolling' : ''
                        }`}
                        onScroll={handleDetailsScroll}>
                        {/* Left Column - Image and Audio */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Watch Image */}
                            <div className="w-[220px] h-[340px] flex-shrink-0 flex items-center justify-center mx-auto">
                                <img
                                    src={watchImage || 'https://via.placeholder.com/400x400?text=Watch+Image'}
                                    alt={watchModelDetails?.full_name || watchModelDetails?.name || 'Watch'}
                                    className="w-full h-full rounded-lg object-contain"
                                />
                            </div>
                            {/* Audio Player */}
                            {audioSource && !storyLoader ? (
                                <div className="bg-gray-50 rounded-lg sm:p-4 p-2 flex items-center gap-4 bg-white border border-gray-200 rouded-lg mx-auto shadow-sm gap-5">
                                    <audio
                                        ref={audioRef}
                                        src={audioSource}
                                        preload="metadata"
                                        className="hidden"
                                    />
                                    <button
                                        onClick={togglePlay}
                                        className="sm:w-14 sm:h-14 h-10 w-10 bg-[#B38B00] rounded-full flex items-center justify-center hover:bg-[#CC9F00] transition-colors"
                                    >
                                        {isPlaying ? (
                                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                            </svg>
                                        ) : (
                                            <svg className="sm:w-6 sm:h-6 h-4 w-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        )}
                                    </button>
                                    <div className="flex-1 mt-4">
                                        <div
                                            className="relative w-full flex items-center cursor-pointer"
                                            onClick={handleSeek}
                                        >
                                            {/* Base track */}
                                            <div
                                                ref={progressBarRef}
                                                className="w-full h-1.5 bg-gray-300 rounded-full overflow-hidden"
                                            >
                                                {/* Filled track */}
                                                <div
                                                    className="h-full bg-[#B38B00]"
                                                    style={{ width: `${audioProgress}%` }}
                                                />
                                            </div>
                                            {/* Knob */}
                                            <div
                                                className="absolute left-0 sm:w-5 sm:h-5 h-3 w-3 rounded-full bg-[#B38B00] -translate-x-1/2"
                                                style={{ marginLeft: `${audioProgress}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-base text-gray-500 mt-2">
                                            <span>{formatTime(currentTime)}</span>
                                            <span>{formatTime(duration)}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : !storyLoader ? (
                                <div className="bg-gray-50 rounded-lg p-4 text-gray-600 text-sm">
                                    Audio preview is not available for this model.
                                </div>
                            ) : (
                                <div className="w-full bg-white border border-gray-200 rounded-lg px-6 py-3 flex items-center gap-5 shadow-sm mx-auto animate-pulse">
                                    <div className="w-14 h-14 bg-gray-300 rounded-full" />
                                    <div className="flex-1 mt-4">
                                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden" />
                                        <div className="flex justify-between text-base text-gray-400 mt-2">
                                            <div className="w-10 h-3 bg-gray-200 rounded" />
                                            <div className="w-10 h-3 bg-gray-200 rounded" />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Description */}
                            <div className="text-gray-700 leading-relaxed">
                                {/* <h2 className="text-2xl font-semibold text-black mb-4">{watchModelDetails?.name || watchModelDetails?.full_name || 'Watch Details'}</h2> */}
                                {activeDescription && !storyLoader ? (
                                    activeDescription.split('\n\n').map((paragraph, index) => (
                                        <p key={index} className="mb-4 text-sm">
                                            {paragraph}
                                        </p>
                                    ))
                                ) : !storyLoader ? (
                                    <p className="mb-4 text-base">No description available.</p>
                                ) : (
                                    storyLoader && (
                                        <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-10 animate-pulse">
                                            <div className="flex-1 space-y-2 ">
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                                <div className="w-full h-3 bg-gray-300 rounded-full" />
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                        {/* Right Column - Specifications */}
                        <div className="lg:col-span-1">
                            {/* <h2 className="text-2xl font-semibold text-black mb-6">Specifications</h2> */}
                            <div className="flex flex-col gap-3 pr-2 justify-between w-full">
                                {specificationEntries.length ? (
                                    specificationEntries
                                        .filter(([key, _]) => key.toLowerCase() !== "image")   // REMOVE image key
                                        .map(([key, value]) => {
                                            const formattedKey = key
                                                .replace(/_/g, ' ')              // replace underscores
                                                .replace(/^\w/, c => c.toUpperCase());  // capitalize first letter

                                            return (
                                                <div key={key} className="flex justify-between pb-2">
                                                    <div className="text-[16px] text-black font-semibold w-1/2">
                                                        {formattedKey}
                                                    </div>
                                                    <div className="text-base font-medium text-gray-500 break-words h-full w-1/2">
                                                        {value}
                                                    </div>
                                                </div>
                                            );
                                        })
                                ) : null}

                            </div>
                        </div>
                    </div>
                </div>
            </div> : (
                <div className="w-full max-w-lg mx-auto mt-20 px-6">
                    <div className="bg-white rounded-xl shadow-lg p-10 flex flex-col items-center text-center">
                        {/* Icon */}
                        <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                            <svg
                                className="w-10 h-10 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>

                        {/* Text */}
                        <h2 className="text-2xl font-bold text-gray-800 mb-3">
                            Watch Not Found
                        </h2>

                        <p className="text-gray-600 text-base max-w-sm">
                            We couldn’t find any details for this watch model.
                            Please check the model number or try again.
                        </p>
                    </div>
                </div>
            )
            }
        </div >
    )
}
export default WatchDetails